<template>
  <div class="cart-item">
    <div class="cart-item__body">
      <order-book-display-item
        class="cart-item__display"
        :order="order"
        :order-line="orderLine"
        :show-quantity-input="showQuantityInput"
      />
      <div class="cart-item__chips">
        <ekitabu-chip
          class="cart-item__format-chip"
          :color="formatChipColor"
          label
          small
          dark
        >
          <ekitabu-icon
            class="mr-1"
            small
            aria-hidden="true"
          >
            {{ formatIcon }}
          </ekitabu-icon>
          {{ formatLabel }}
        </ekitabu-chip>
      </div>
    </div>
    <!-- Right column: delete at top, quantity stepper at bottom (Uber Eats style) -->
    <div class="cart-item__right">
      <remove-from-cart-button
        :id="orderLine.id"
        :order-id="orderId"
        @updateLoading="(event) => (removeLoading = event)"
        @onRemoved="(event) => $emit('onRemoved', event)"
      />
      <order-line-quantity-input
        v-if="showQuantityInput && orderLine?.format === formats.print"
        :order-line-id="orderLine.id"
        :quantity="orderLine.quantity"
        small
        @input="(qty) => $emit('quantityInput', { orderLineId: orderLine.id, quantity: qty })"
        @quantityUpdated="$emit('quantityChanging')"
        @quantityFailed="$emit('quantityFailed')"
      />
    </div>
    <div
      v-if="removeLoading"
      class="cart-item__loading-overlay"
      role="status"
      aria-live="polite"
    >
      <EkitabuProgressCircular
        color="primaryDark"
        indeterminate
        size="24"
        width="2"
      />
    </div>
  </div>
</template>

<script>
import RemoveFromCartButton from "./RemoveFromCartButton";
import currencyMixins from "@/mixins/currencyMixins";
import OrderBookDisplayItem from "@/components/OrderBookDisplayItem";
import OrderLineQuantityInput from "@/components/inputs/OrderLineQuantityInput.vue";
import { FORMATS } from "@/utils/Const";
import { getFormatColor, getFormatIcon, getFormat } from "@/utils/BookFormatHelper";

export default {
  name: "CartListItem",
  components: {
    RemoveFromCartButton,
    OrderBookDisplayItem,
    OrderLineQuantityInput,
  },
  mixins: [currencyMixins],
  props: {
    order: {
      type: Object,
      required: true,
    },
    orderLine: {
      type: Object,
      required: true,
    },
    showQuantityInput: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      removeLoading: false,
    };
  },
  computed: {
    orderId() {
      return this.order.id;
    },
    formats() {
      return FORMATS;
    },
    formatLabel() {
      return getFormat(this.orderLine?.format || '');
    },
    formatIcon() {
      return getFormatIcon(this.orderLine?.format);
    },
    formatChipColor() {
      return getFormatColor(this.orderLine?.format);
    },
  },
};
</script>

<style scoped lang="scss">
.cart-item {
  position: relative;
  display: flex;
  align-items: stretch;
  gap: 12px;
  padding: 12px 0;
  font-family: $book-font-body;
  transition: background-color 0.15s ease;
}

.cart-item:hover {
  background-color: $book-hover-bg;
}

.cart-item__body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.cart-item__display {
  flex: 1;
  min-width: 0;
}

/* Right column: delete icon at top, qty stepper at bottom (Uber Eats pattern) */
.cart-item__right {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
}

/* Book cover — match checkout 40×56 */
::v-deep .cart-item__display .book-cover-image {
  width: 40px;
  height: 56px;
  border-radius: 4px;
  overflow: hidden;
}

/* Shared base for all deep text in the display area */
::v-deep .cart-item__display h3.subtitle-2,
::v-deep .cart-item__display h4,
::v-deep .cart-item__display .gradient-text {
  font-family: $book-font-body;
  line-height: 1.3;
  overflow: hidden;
}

/* Title + price share weight and size */
::v-deep .cart-item__display h3.subtitle-2,
::v-deep .cart-item__display .gradient-text {
  font-size: 0.8125rem;
  font-weight: 700;
  color: $book-ink;
}

/* Title truncation */
::v-deep .cart-item__display h3.subtitle-2 {
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

/* Author text */
::v-deep .cart-item__display h3.subtitle-2.font-weight-regular,
::v-deep .cart-item__display h4 {
  font-size: 0.6875rem;
  font-weight: 500;
  color: $book-grey;
}

/* Price — strip gradient-text background */
::v-deep .cart-item__display .gradient-text {
  background: none;
  -webkit-background-clip: unset;
  -webkit-text-fill-color: $book-ink;
}

::v-deep .cart-item__display .discount-text,
::v-deep .cart-item__display .discount-text.gradient-text {
  -webkit-text-fill-color: $book-green;
  color: $book-green;
}

::v-deep .cart-item__display .striked-text.gradient-text {
  -webkit-text-fill-color: $book-grey;
  color: $book-grey;
}

/* Format chip row — stays below book info, indented to align with text */
.cart-item__chips {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  padding-left: 52px;
}

.cart-item__format-chip {
  border-radius: 4px !important;
  font-family: $book-font-body;
  font-size: 0.6875rem;
  font-weight: 600;
  height: 22px !important;
}

.cart-item__loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.75);
  backdrop-filter: blur(1px);
  border-radius: 4px;
}

/* Delete button (top of right column) */
::v-deep .cart-item__right .v-btn--icon {
  width: 26px !important;
  height: 26px !important;
  opacity: 0.4;
  transition: opacity 0.15s ease;
}

.cart-item:hover ::v-deep .cart-item__right .v-btn--icon {
  opacity: 1;
}

::v-deep .cart-item__right .v-btn--icon .v-icon {
  font-size: 14px;
  color: $book-grey-light;
  transition: color 0.15s ease;
}

.cart-item:hover ::v-deep .cart-item__right .v-btn--icon .v-icon {
  color: $book-red-danger;
}
</style>
